import type { Permissions } from '@/context/auth/types';
import type { CustomerType as CustomerTypeValue } from '@/enums';

import {
  ORDER_CREATE_PERMISSION_PREFIXES,
  ORDER_DOWNLOAD_PAYLOAD_PERMISSION_PREFIXES,
  ORDER_UPDATE_PERMISSION_PREFIXES,
  PERMISSIONS,
} from '@/constants/permissions';
import { CustomerType } from '@/enums';

const toPermissionSegment = (value: string) => value.trim().toLowerCase().replace(/\s+/g, '-');
const toCreatePermissionSegment = (value: string) => value.trim().toLowerCase().replace(/\s+/g, '');

export const hasScopedPermissionPrefix = (permission: string, prefix: Permissions) =>
  permission === prefix || permission.startsWith(`${prefix}.`);

export const hasPermission = (organizationPermissions: Permissions[], name?: Permissions) =>
  !!name && organizationPermissions.includes(name);

export const hasAnyPermission = (organizationPermissions: Permissions[], names: Permissions[]) =>
  names.some((name) => organizationPermissions.includes(name));

export const hasAnyPermissionPrefix = (
  organizationPermissions: Permissions[],
  prefixes: Permissions[]
) =>
  organizationPermissions.some((permission) =>
    prefixes.some((prefix) => hasScopedPermissionPrefix(permission, prefix))
  );

export const canCreateOrder = (organizationPermissions: Permissions[]) =>
  hasAnyPermissionPrefix(organizationPermissions, ORDER_CREATE_PERMISSION_PREFIXES);

export const canCreateOrderForProductCategory = (
  organizationPermissions: Permissions[],
  productCategory?: string
): boolean => {
  if (hasPermission(organizationPermissions, PERMISSIONS.ORDER.CREATE)) {
    return true;
  }

  if (!productCategory) {
    return false;
  }

  return hasPermission(
    organizationPermissions,
    `${PERMISSIONS.ORDER.CREATE}.${toCreatePermissionSegment(productCategory)}`
  );
};

export const canUpdateOrder = (organizationPermissions: Permissions[]) =>
  hasAnyPermissionPrefix(organizationPermissions, ORDER_UPDATE_PERMISSION_PREFIXES);

export const getProductScopedPermission = (permissionPrefix: Permissions, productCategory: string) =>
  `${permissionPrefix}.${toPermissionSegment(productCategory)}`;

export const canDownloadOrderPayload = (
  organizationPermissions: Permissions[],
  productCategory?: string
): boolean => {
  if (hasPermission(organizationPermissions, PERMISSIONS.ORDER.DOWNLOAD_PAYLOAD)) {
    return true;
  }

  if (productCategory) {
    return hasPermission(
      organizationPermissions,
      getProductScopedPermission(PERMISSIONS.ORDER.DOWNLOAD_PAYLOAD, productCategory)
    );
  }

  return hasAnyPermissionPrefix(
    organizationPermissions,
    ORDER_DOWNLOAD_PAYLOAD_PERMISSION_PREFIXES
  );
};

export const canViewDeliveryStatus = (
  organizationPermissions: Permissions[],
  productCategory?: string
): boolean => {
  if (hasPermission(organizationPermissions, PERMISSIONS.ORDER.VIEW_DELIVERY_STATUS)) {
    return true;
  }

  if (productCategory) {
    return hasPermission(
      organizationPermissions,
      getProductScopedPermission(PERMISSIONS.ORDER.VIEW_DELIVERY_STATUS, productCategory)
    );
  }

  return false;
};

export const canAccessOrderPayloadLinks = ({
  organizationCustomerType,
  organizationPermissions,
  productCategory,
}: {
  organizationCustomerType?: CustomerTypeValue;
  organizationPermissions: Permissions[];
  productCategory?: string;
}) =>
  organizationCustomerType === CustomerType.admin ||
  canDownloadOrderPayload(organizationPermissions, productCategory);
